import React, { useRef, useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { Grid, Paper, LinearProgress } from '@material-ui/core';
import { useStateValue } from 'context/store';
import { listDeliveryRoute } from 'context/booking/actions';
import { routeIsTerminal } from 'utils/deliveryRoute';
import { withDisplayStopNumbers } from 'utils/deliveryRouteNumbering';
import Loading from 'components/Loading';
import RouteHeader from 'components/DeliveryRoute/RouteHeader';
import RouteSummary from 'components/DeliveryRoute/RouteSummary';
import RouteTabs from 'components/DeliveryRoute/RouteTabs';
import EmptyForCompany from 'components/DeliveryRoute/EmptyForCompany';
import RouteDriver from 'components/DeliveryRoute/RouteDriver';
import RouteEstimatedData from 'components/DeliveryRoute/RouteEstimatedData';

const DeliveryRouteMap = React.lazy(() => import('components/DeliveryRoute/DeliveryRouteMap'));

const POLL_INTERVAL_MS = 10000;

export default function DeliveryRouteDetail({ bookingId, companyId, onClickBack }) {
  const [{ booking: { deliveryRoute } }, dispatch] = useStateValue();
  const [loading, setLoading] = useState(false);
  // Stops selected on the map (every stop of a place, or one row of its popup) or on the list.
  const [selectedStopIds, setSelectedStopIds] = useState([]);
  const mapRef = useRef(null);
  // Lista y mapa numeran las paradas con la misma fachada, para que coincidan entre sí.
  const numberedRoute = useMemo(() => withDisplayStopNumbers(deliveryRoute), [deliveryRoute]);

  const fetchRoute = useCallback(
    async (init) => {
      init && setLoading(true);
      await listDeliveryRoute(dispatch, bookingId, { company_id: companyId });
      init && setLoading(false);
    },
    [dispatch, bookingId, companyId]
  );

  useEffect(() => {
    fetchRoute('init');
  }, [fetchRoute]);

  useEffect(() => {
    if (!deliveryRoute || routeIsTerminal(deliveryRoute.status_cd)) return undefined;

    const timer = setTimeout(() => fetchRoute(), POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [deliveryRoute, fetchRoute]);

  const handleFocusStop = (stopId) => {
    setSelectedStopIds([stopId]);
    if (mapRef.current) mapRef.current.flyToStop(stopId);
  };

  if (loading || !deliveryRoute) {
    return <Loading />;
  }

  // No outer Container/Paper here: BookingDetail already wraps this page in its own
  // .booking-detail Container + .paper-booking Paper. Adding a second .paper-booking
  // (width: 85vw; margin: auto) inside that first one compounds the width/centering rules
  // and overflows the outer card instead of filling it.
  return (
    <div className="delivery-route-detail" data-testid="delivery-route-detail">
      <RouteHeader route={deliveryRoute} onClickBack={onClickBack} />

      {!deliveryRoute.has_packages_for_company ? (
        <EmptyForCompany />
      ) : (
        <Grid container spacing={5}>
          <Grid item md={6} sm={12} xs={12}>
            <Paper elevation={0} style={{ border: '1px solid #E0DCE6', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <RouteDriver route={deliveryRoute} />
              <div style={{ marginTop: deliveryRoute.driver ? 16 : 0 }}>
                <RouteSummary route={deliveryRoute} />
              </div>
            </Paper>
            <RouteTabs route={numberedRoute} onFocusStop={handleFocusStop} selectedStopIds={selectedStopIds} />
          </Grid>
          <Grid item md={6} sm={12} xs={12} style={{ paddingTop: 0 }}>
            <Suspense fallback={<LinearProgress color="primary" />}>
              <DeliveryRouteMap route={numberedRoute} ref={mapRef} onSelectStops={setSelectedStopIds} />
            </Suspense>

            <RouteEstimatedData route={deliveryRoute} />
          </Grid>
        </Grid>
      )}
    </div>
  );
}
